import { AuthService } from './../../services/auth.service';
import { Component, EventEmitter, Input, Output, OnInit, ViewChild, forwardRef, ChangeDetectionStrategy } from '@angular/core';
import { ClientService } from '../../services/client/client.service';
import { ClientDTO, IPagedResultDTOOfClientDTO } from '../../models/models.swagger.generated';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { AutocompleteComponent } from '../autocomplete/autocomplete.component';
import { ControlValueAccessor, FormControl, NG_VALIDATORS, NG_VALUE_ACCESSOR, AbstractControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { IdProvider } from '../id-provider';
import { IAutocompleteItem } from '../autocomplete/autocomplete-item';


@Component({
    selector: 'app-client-autocomplete',
    templateUrl: './client-autocomplete.component.html',
    styleUrls: ['./client-autocomplete.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ClientAutocompleteComponent),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => ClientAutocompleteComponent),
            multi: true
        }
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [AutocompleteComponent]
})

export class ClientAutocompleteComponent implements ControlValueAccessor, OnInit {
  @ViewChild(AutocompleteComponent)
  public autoCompleteComponent: AutocompleteComponent | null = null; // Reference to the AutocompleteComponent

  @Output() public selectedClientChange: EventEmitter<ClientDTO | null> = new EventEmitter<ClientDTO | null>();

  @Input() public placeholder = 'i18n.shared.search-client';

  @Input()
  public id: string = `app-client-autocomplete-${IdProvider.getNextId()}`;

  @Input() public set selectedClient(client: ClientDTO | null) {
    if (this._selectedClient?.id != client?.id) {
      this.applySelectedClient(client);
    }
  }

  @Input() public showSystem = false;

  public get isFormComponent(): boolean {
    return this._control != null;
  }

  private systemClient = { id: 0, name: this.translateService.instant('i18n.base.shared.client.system') } as ClientDTO;

  public get selectedClientId(): number | null {
    if (this._selectedClient === null) {
      return null;
    }
    return this._selectedClient?.id;
  }

  public set selectedClientId(value: number | null) {
    if (this.selectedClientId != value) {
      let client = this.getClientById(value);
      if(value === 0 && this.showSystem){
        client = this.systemClient;
      }
      this.applySelectedClient(client);
      this.onTouched();
    }
  }

  public get isClientAdminView(): boolean {
    return this.authService.hasUserClientAssigned();
  }

  public fields: Object = { text: 'name', value: 'id' };
  public get selectedClientName(): string | null {
    return this.getClientsDisplayName(this._selectedClient);
  }
  public isDisabled = false;

  private _selectedClient: ClientDTO | null = null;
  private clientsCache: ClientDTO[] = [];
  private _control: AbstractControl | null = null;

  constructor(
    private clientService: ClientService,
    private authService: AuthService,
    private translateService: TranslateService
  ) {
    this.translateService.get('i18n.base.shared.client.system').subscribe((translated: string) => {
      this.systemClient.name = translated;
    });
  }

  public onChange: any = () => {};
  public onTouched: any = () => {};

  public writeValue(client: ClientDTO | null): void {
    this.selectedClient = client;
  }

  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  public setDisabledState?(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  public validate(control: FormControl) {
    this._control = control;
    return null;
  }

  ngOnInit(): void {
    if (this.authService.hasUserClientAssigned()) {
      this._selectedClient = this.authService.getUserInfo()?.client ?? null;
      return;
    }

    // Sync from the shared service so a previously selected client is shown
    // when the component is opened on another page.
    const shared = this.clientService.selectedClient;
    if (shared && this._selectedClient?.id !== shared.id) {
      this._selectedClient = shared;
      if (!this.clientsCache.find(c => c.id === shared.id)) {
        this.clientsCache.push(shared);
      }
    }
  }

  public searchClientData(searchText: string | null): Observable<IAutocompleteItem[]> {
    if (searchText === null || searchText?.trim() === '') {
      return of(this.clientsCache.map((client: ClientDTO) => {
        return { id: client.id, name: this.getClientsDisplayName(client) || '' };
      }));
    }
    return this.clientService.searchClients(searchText, 20).pipe(map((clientPaged: IPagedResultDTOOfClientDTO) => {
      this.clientsCache = clientPaged.items || [];
      if (this.showSystem && this.authService.isUserAdmin()) {
        this.clientsCache.push(this.systemClient);
      }
      const clientId = this._selectedClient?.id;
      if (clientId && !this.clientsCache.find(c => c.id === clientId)) {
        this.clientsCache.push(this._selectedClient!);
      }
      return this.clientsCache.map((client: ClientDTO) => {
        return { id: client.id, name: this.getClientsDisplayName(client) || '' };
      });
    }));
  }

  public focus(): void {
    this.autoCompleteComponent?.focus();
  }

  private getClientById(id: number | null): ClientDTO | null {
    if (!id) {
      return null;
    }
    return this.clientsCache.find(c => c.id == id) || null;
  }

  private applySelectedClient(client: ClientDTO | null): void {
    this._selectedClient = client;
    if (client && !this.clientsCache.find(c => c.id === client.id)) {
      this.clientsCache.push(client);
      this.autoCompleteComponent?.refresh();
    }
    // Keep the shared service in sync so other pages/forms show the same client.
    this.clientService.selectedClient = client;
    this.selectedClientChange.emit(client);
    this.onChange(client);
  }

  private getClientsDisplayName(client: ClientDTO | null | undefined): string | null {
    if (!client) {
      return null;
    }
    return client.name || null;
  }
}
